import { useEffect, useState } from "react";
import firebase from "firebase/app";
import "firebase/auth";
import { useAuthState as useFirebaseAuthState } from "react-firebase-hooks/auth";
import { useRecoilState, useRecoilValue } from "recoil";
import { requestCloudFunction, sendRequest } from "./fetch";
import { InternalServerError } from "./error";
import { AuthError, TooManyRequestError } from "./auth/error";
import { useDocumentWithDependencies } from "./firebase/depended";
import {
  authLoadingState,
  userIdTokenState,
  currentViewerState,
  firebaseAuthState,
} from "state/auth";
import type { Viewer, WithId, AuthOption } from "@kaigi100-next/typing";
import { viewerConverter } from "./firebase/converters/viewer";

type JoinProcessState = "IDLE" | "PROCESSING" | "FULFILLED" | "ERROR";
export function useJoin() {
  const [joinProcessState, setJoinProcessState] = useState<JoinProcessState>(
    "IDLE"
  );

  /**
   * 会議へログインする
   */
  async function join(conferenceId: string, option?: Partial<AuthOption>) {
    setJoinProcessState("PROCESSING");
    const userCredential = await firebase
      .auth()
      .signInAnonymously()
      .catch(async (reason) => {
        // if (reason.code === "auth/too-many-requests") {
        //   setError(new TooManyRequestError());
        // } else {
        //   setError(new AuthError());
        // }
      });
    console.log(userCredential);
    if (!userCredential) {
      setJoinProcessState("ERROR");
      return;
    }
    await setAuthToDatabase(userCredential.user, conferenceId, option);
    setJoinProcessState("FULFILLED");
  }

  async function migrate(user: firebase.User, conferenceId: string, option?: Partial<AuthOption>) {
    setJoinProcessState("PROCESSING");
    await setAuthToDatabase(user, conferenceId, option);
    setJoinProcessState("FULFILLED");
  }
  return { join, joinProcessState, migrate };
}

type SignInProcessState = JoinProcessState;
type SignInErrorCode =
  | "auth/invalid-email"
  | "auth/user-disabled"
  | "auth/user-not-found"
  | "auth/wrong-password";
export function useSignIn() {
  const [signInProcessState, setSignInProcessState] = useState<
    SignInProcessState
  >("IDLE");
  const [error, setError] = useState<SignInErrorCode>();
  async function signIn(email: string, password: string) {
    setSignInProcessState("PROCESSING");
    try {
      const { user } = await firebase
        .auth()
        .signInWithEmailAndPassword(email, password);
      const token = await user.getIdToken();
      await sendRequest(token, "/auth/log/logged-in");
      setSignInProcessState("FULFILLED");
    } catch (error) {
      console.log(error);
      setError(error.code);
      setSignInProcessState("ERROR");
    }
  }
  return { signInProcessState, error, signIn };
}

async function setAuthToDatabase(
  user: firebase.User,
  conferenceId: string,
  option?: Partial<AuthOption>
) {
  const idToken = await user.getIdToken();
  console.log(idToken);
  try {
    const result = await requestCloudFunction(
      "/viewers/add",
      idToken,
      {
        conferenceId,
        option,
      }
    );
    // setJoinProcessState("FULFILLED");
  } catch (error) {
    if (error instanceof InternalServerError) {
      await firebase.auth().signOut();
      // setError(error);
      // setJoinProcessState("ERROR");
    }
    console.log(error);
  }
}

export function useAuthState() {
  /**
   * もろもろを加味した認証状態
   *
   * firebase authenticationの状態やfirestoreの登録状況を踏まえて
   * アプリケーションが取るべき認証状態を表現する
   */
  const [loading, setLoading] = useRecoilState(authLoadingState);
  const [, setUserIdToken] = useRecoilState(userIdTokenState);
  const [, setAuth] = useRecoilState(firebaseAuthState);
  const [error, setError] = useState<Error>();

  /**
   * firebase authenticationの状態
   */
  const [
    firebaseAuth,
    firebaseAuthLoading,
    firebaseAuthError,
  ] = useFirebaseAuthState(firebase.auth());

  /**
   * firestore に保持している参加者idなどの情報
   */
  const [auth, authLoading, authError] = useDocumentWithDependencies<
    Auth,
    { firebaseAuth: firebase.User }
  >({ firebaseAuth }, ({ firebaseAuth }) => {
    // console.log(firebaseAuth);
    return firebase.firestore().doc(`user/v1/auth/${firebaseAuth.uid}`);
  });

  /**
   * firebase に保持している参加者情報
   * recoilを使ってグローバルな状態も更新する
   */
  const [viewer, viewerLoading, viewerError] = useDocumentWithDependencies<
    WithId<Viewer>,
    { auth: Auth }
  >(
    { auth },
    ({ auth }) =>
      firebase
        .firestore()
        .doc(`user/v1/viewers/${auth.viewerId}`)
        .withConverter(viewerConverter),
    { idField: "id" }
  );
  const [, setCurrentViewer] = useRecoilState(currentViewerState);

  /**
   * 認証状況の確認
   *
   * firebase authenticationがログアウトの場合、そこで終了
   */
  useEffect(() => {
    if (firebaseAuthLoading) {
      return;
    }
    if (!firebaseAuth) {
      setLoading(false);
      return;
    }
    setAuth(firebaseAuth);
    firebaseAuth.getIdToken().then((userIdToken) => {
      setUserIdToken(userIdToken);
    });
  }, [firebaseAuth, firebaseAuthLoading]);

  /**
   * firestoreに認証情報が保存されているか確認する
   * firestoreに認証情報が保存されていなければ作成する
   */
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!auth) {
      // setAuthToDatabase(firebaseAuth, conferenceId);
      setLoading(false);
      return;
    }
  }, [authLoading, auth]);

  /**
   * firestoreに参加者情報が保存されているか確認する
   * firestoreに参加者情報が保存されていなければ作成する
   * 参加者情報が確認できたらrecoilのcurrentViewerStateに登録し読み込みを完了する
   */
  useEffect(() => {
    if (viewerLoading) {
      return;
    }

    if (!viewer) {
      // setAuthToDatabase(firebaseAuth, conferenceId);
      setLoading(false);
      return;
    }
    setCurrentViewer(viewer);

    setLoading(false);
  }, [viewerLoading, viewer]);

  // console.log(firebaseAuthLoading, authLoading);
  // console.log(firebaseAuthError, authError);
  // console.log(firebaseAuth, auth, viewer);
  // console.log(authLoading);
  return { loading, auth, viewer, error };
}
